import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { withStyles, Paper } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import CircularProgress from 'components/shared/CircularProgress';
import { isImpersonating as isImpersonatingSelector } from 'selectors/user';
import { hasReachableCounterpart } from '../conversationLock';
import Messages, { StugknutenMessage } from './Messages';
import Footer from './Footer';

const MOBILE_CHROME = 56 + 48;
const DESKTOP_CHROME = 64 + 48;
const BANNER_HEIGHT = 48;
// iOS slides the keyboard in over ~300ms; re-pin to the bottom after it settles.
const IOS_KEYBOARD_ANIMATION_MS = 300;

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0px -1px 2px -1px rgba(0, 0, 0, 0.2)',
    height: `calc(100vh - ${MOBILE_CHROME}px)`,
    [theme.breakpoints.up('sm')]: {
      height: `calc(100vh - ${DESKTOP_CHROME}px)`,
    },
    '@supports (height: 100dvh)': {
      height: `calc(100dvh - ${MOBILE_CHROME}px)`,
      [theme.breakpoints.up('sm')]: {
        height: `calc(100dvh - ${DESKTOP_CHROME}px)`,
      },
    },
  },
  rootWithBanner: {
    height: `calc(100vh - ${MOBILE_CHROME + BANNER_HEIGHT}px)`,
    [theme.breakpoints.up('sm')]: {
      height: `calc(100vh - ${DESKTOP_CHROME + BANNER_HEIGHT}px)`,
    },
    '@supports (height: 100dvh)': {
      height: `calc(100dvh - ${MOBILE_CHROME + BANNER_HEIGHT}px)`,
      [theme.breakpoints.up('sm')]: {
        height: `calc(100dvh - ${DESKTOP_CHROME + BANNER_HEIGHT}px)`,
      },
    },
  },
  paper: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  chat: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(2),
  },
  noteMsg: {
    backgroundColor: '#C5E1A5',
    borderRadius: 4,
    fontSize: 12,
    margin: '0 auto',
    maxWidth: 580,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
});

const MessageTab = ({
  classes,
  currentUser,
  data,
  showNoteMessage,
  showLoader,
  hasError,
  deleteMessage,
  conversationId,
  isHost,
}) => {
  const chatRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const formRef = React.useRef(null);
  const focusScrollTimer = React.useRef();
  const isImpersonating = useSelector(isImpersonatingSelector);

  const scrollChatToBottom = React.useCallback(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, []);

  React.useEffect(() => {
    // Lock page scroll via a class, NOT an inline style: MUI Dialogs snapshot &
    // restore the inline `body.style.overflow`, so an inline lock could be
    // stranded after this view unmounts (broke scrolling on pages reached from
    // an in-conversation dialog, e.g. "Manage My files").
    document.body.classList.add('conversation-scroll-lock');
    return () => {
      document.body.classList.remove('conversation-scroll-lock');
      window.clearTimeout(focusScrollTimer.current);
    };
  }, []);

  // Re-pin to the latest message when the message count changes (a message
  // arrived or was removed) -- not on every `data` update, so an unrelated
  // change (lock_reason, filters) doesn't yank a scrolled-up user to the bottom.
  const messageCount = data?.Messages?.length ?? 0;

  // useLayoutEffect (not useEffect): pin to the bottom synchronously after the
  // DOM commit but BEFORE paint, so a long conversation never flashes scrolled
  // to the top for a frame before jumping down.
  React.useLayoutEffect(() => {
    if (!showLoader && messageCount > 0) {
      scrollChatToBottom();
    }
  }, [showLoader, messageCount, scrollChatToBottom]);

  const handleFocus = () => {
    // After the iOS keyboard animates in, re-pin to bottom so the message
    // being replied to stays visible. Tracked so it can't fire after unmount.
    window.clearTimeout(focusScrollTimer.current);
    focusScrollTimer.current = window.setTimeout(scrollChatToBottom, IOS_KEYBOARD_ANIMATION_MS);
  };

  // The backend decides whether the conversation is locked.
  const lockReason = data?.lock_reason ?? null;
  // Don't allow sending until the conversation is loaded and has someone to
  // reach -- otherwise a message could be posted into an unloaded or
  // counterpart-less conversation (no banner; that's reserved for the lock).
  const footerDisabled =
    !data || !data.Messages || !hasReachableCounterpart(data.Users, currentUser?.id);

  let chatBody;
  if (showLoader) {
    chatBody = <CircularProgress />;
  } else if (hasError) {
    // Missing conversation or no access -- show a message instead of spinning.
    chatBody = (
      <StugknutenMessage>
        <FormattedMessage
          id="conversations.loadError"
          defaultMessage="This conversation could not be found, or you no longer have access to it."
        />
      </StugknutenMessage>
    );
  } else {
    chatBody = (
      <>
        {showNoteMessage && (
          <div>
            <StugknutenMessage>
              <FormattedMessage
                id="conversations.sCertifiedNote"
                defaultMessage="This accommodation is S-certified. Please remember
                    that you must make payment via Stugknuten's platform in order
                    to qualify for the protection provided by S-certification."
              />
            </StugknutenMessage>
          </div>
        )}
        <Messages
          data={data}
          currentUserId={currentUser.id}
          deleteMessage={deleteMessage}
          isHost={isHost}
        />
      </>
    );
  }

  return (
    <div className={classNames(classes.root, isImpersonating && classes.rootWithBanner)}>
      <Paper className={classes.paper}>
        <div ref={chatRef} className={classes.chat}>
          {chatBody}
        </div>
      </Paper>
      <Footer
        isShowContactsWarning={
          data?.filters?.commission &&
          data?.filters?.conversation_bookings === 0 &&
          data?.filters?.total_host_bookings_18m < 2
        }
        isShowTerminationWarning={isHost}
        isHost={isHost}
        disableFileInput={data?.filters?.files}
        cottageId={data?.cottage_id}
        setInputRef={inputRef}
        setFormRef={formRef}
        onFocus={handleFocus}
        conversationId={conversationId}
        lockReason={lockReason}
        disabled={footerDisabled}
      />
    </div>
  );
};

MessageTab.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    Messages: PropTypes.arrayOf(PropTypes.shape({})),
    Users: PropTypes.arrayOf(PropTypes.shape({})),
    lock_reason: PropTypes.string,
    cottage_id: PropTypes.number,
    filters: PropTypes.shape({
      emails: PropTypes.bool,
      phone_numbers: PropTypes.bool,
      files: PropTypes.bool,
      links: PropTypes.bool,
      commission: PropTypes.bool,
      bank_information: PropTypes.bool,
      conversation_bookings: PropTypes.number,
      total_host_bookings_18m: PropTypes.number,
      termination_warning: PropTypes.bool,
    }),
  }),
  conversationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showLoader: PropTypes.bool,
  hasError: PropTypes.bool,
  showNoteMessage: PropTypes.bool,
  isHost: PropTypes.bool.isRequired,

  deleteMessage: PropTypes.func.isRequired,
};

export default withStyles(styles)(MessageTab);
